import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import {
  checkStatusCodeSuccess,
  notFound,
  PaymentMode,
} from "Components/constants/Common";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "../../../Components/constants/validation";
import { BaseSelect } from "Components/Base/BaseSelect";
import { cancelLabel, submitLabel } from "Components/constants/Role";
import { orderPaymentApi, viewOrderHistory } from "Api/Order";
import TableContainer from "Components/Base/BaseTable";
import { RiSearchLine } from "react-icons/ri";
import moment from "moment";
import {
  ADD_PAYMENT_ORDER_LABEL,
  CURRENT_OUTSTANDING_AMOUNT_LABEL,
  CURRENT_PAYABLE_AMOUNT_LABEL,
  OUTSTANDING_AMOUNT,
  OUTSTANDING_AMOUNT_KEY,
  PAYABLE_AMOUNT,
  PAYABLE_AMOUNT_KEY,
  PAYMENT_DATE,
  PAYMENT_MODE,
  PAYMENT_MODE_KEY,
  RECEIVED_AMOUNT,
  RECEIVED_AMOUNT_ERROR,
  RECEIVED_AMOUNT_KEY,
  RECEIVED_DATE,
  REMARKS_KEY,
  REMARKS_LABEL,
  TOTAL_AMOUNT_KEY,
  TOTAL_AMOUNT_LABEL,
} from "Components/constants/Order";
import { ReceivedDateLabel } from "Components/constants/PurchaseOrder";
import {
  addPurchaseOrderPayment,
  viewPurchaseOrder,
  viewPurchaseOrderPaymentHistory,
} from "Api/PurchaseOrder";

const validationSchema = yup.object({
  received_amount: yup
    .number()
    .min(1, validationMessages.positiveNumber(RECEIVED_AMOUNT))
    .required(validationMessages.required(RECEIVED_AMOUNT))
    .test(
      "received-amount-less-than-outstanding",
      RECEIVED_AMOUNT_ERROR,
      function (value) {
        const { payable_amount } = this.parent;
        return value <= payable_amount;
      }
    ),
  payment_mode: yup
    .string()
    .required(validationMessages.required(PAYMENT_MODE)),
  received_date: yup
    .string()
    .required(validationMessages.required(PAYMENT_DATE)),
});

interface OrderHistory {
  id: number | null;
  order_id: number | null;
  total_amount: number;
  received_amount: number;
  out_standing_amount: number;
  payable_amount: number;
  received_date: string;
  payment_mode: string;
  remarks: string;
}

interface History {
  id?: number;
  total_price: number;
  out_standing_amount?: number;
  orderHistory: OrderHistory[];
}

interface PaymentType {
  total_amount: number;
  received_amount: number | null;
  out_standing_amount: number;
  payable_amount: number;
  received_date: string;
  payment_mode: string;
  remarks: string;
}

const AddPurchaseOrderPayment = () => {
  document.title = "Add Purchase Order Payment";
  const navigate = useNavigate();
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(5);
  const [btnLoader, setBtnLoader] = useState(false);
  const [history, setHistory] = useState<History>();

  const fetchProduct = () => {
    setLoader(true);
    viewPurchaseOrderPaymentHistory(Number(id))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setHistory(resp?.data);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const initialValues: PaymentType = {
    total_amount: 0,
    received_amount: null,
    out_standing_amount: 0,
    payable_amount: 0,
    received_date: "",
    payment_mode: "",
    remarks: "",
  };
  const formik = useFormik<PaymentType>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    onSubmit: (values) => {
      setBtnLoader(true);
      addPurchaseOrderPayment(Number(id), values)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message);
            navigate("/purchase-order");
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message);
          return err;
        })
        .finally(() => {
          setBtnLoader(false);
        });
    },
  });

  const orderColumns = useMemo(
    () => [
      {
        header: PAYABLE_AMOUNT,
        accessorKey: PAYABLE_AMOUNT_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.payable_amount}
            </span>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: RECEIVED_AMOUNT,
        accessorKey: RECEIVED_AMOUNT_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.received_amount}
            </span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: OUTSTANDING_AMOUNT,
        accessorKey: OUTSTANDING_AMOUNT_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.out_standing_amount}
            </span>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ReceivedDateLabel,
        accessorKey: RECEIVED_DATE,
        cell: (cell: any) => {
          return (
            <div>
              <span>
                {moment(cell?.row?.original?.received_date).format("ll")}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: PAYMENT_MODE,
        accessorKey: PAYMENT_MODE_KEY,
        cell: (cell: any) => {
          return (
            <div>
              <span className="badge bg-dark">
                {cell?.row?.original?.payment_mode}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: REMARKS_LABEL,
        accessorKey: REMARKS_KEY,
        enableSorting: true,
        enableColumnFilter: false,
      },
    ],
    []
  );

  useEffect(() => {
    if (formik.values.received_amount) {
      let outstandingAmount =
        formik.values.payable_amount - formik.values.received_amount;

      if (outstandingAmount < 0) {
        outstandingAmount = 0;
      }

      formik.setFieldValue(OUTSTANDING_AMOUNT_KEY, outstandingAmount);
    } else {
      formik.setFieldValue(
        OUTSTANDING_AMOUNT_KEY,
        history?.out_standing_amount
      );
    }
  }, [formik.values.received_amount]);

  useEffect(() => {
    formik.setFieldValue(TOTAL_AMOUNT_KEY, history?.total_price);
    formik.setFieldValue(PAYABLE_AMOUNT_KEY, history?.out_standing_amount);
  }, [history]);

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  useEffect(() => {
    fetchProduct();
  }, []);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">Add Purchase Order Payment</span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/purchase-order")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="text-left my-2">
                <div>
                  <div className="d-flex justify-content-between pb-2">
                    <h5 className="">
                      {TOTAL_AMOUNT_LABEL}
                      <span id="payable-amount">
                        <i className="mdi mdi-currency-rupee"></i>
                        {formik.values.total_amount}
                      </span>
                    </h5>
                    <h5 className="">
                      {CURRENT_PAYABLE_AMOUNT_LABEL}
                      <span id="payable-amount">
                        <i className="mdi mdi-currency-rupee"></i>
                        {formik.values.payable_amount}
                      </span>
                    </h5>
                  </div>
                  <div className="">
                    <div className="row">
                      <div className="flex-wrap row">
                        <div className="col-4">
                          <BaseInput
                            name="received_amount"
                            label={RECEIVED_AMOUNT}
                            prepend={<i className="mdi mdi-currency-rupee"></i>}
                            type="number"
                            value={formik.values.received_amount}
                            placeholder="0"
                            onChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            error={formik.errors.received_amount}
                            touched={formik.touched.received_amount}
                            required
                          />
                        </div>
                        <div className="col-4">
                          <BaseSelect
                            name="payment_mode"
                            label={PAYMENT_MODE}
                            options={PaymentMode || []}
                            value={formik.values.payment_mode}
                            placeholder={SelectPlaceHolder(PAYMENT_MODE)}
                            handleChange={(field: any, value: any) => {
                              formik.setFieldValue(field, value);
                            }}
                            handleBlur={formik.handleBlur}
                            error={formik.errors.payment_mode}
                            touched={formik.touched.payment_mode}
                            required
                          />
                        </div>
                        <div className="col-4">
                          <BaseInput
                            name="received_date"
                            label={PAYMENT_DATE}
                            type="date"
                            value={formik.values.received_date}
                            placeholder={InputPlaceHolder(PAYMENT_DATE)}
                            onChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            error={formik.errors.received_date}
                            touched={formik.touched.received_date}
                            required
                          />
                        </div>
                        <div className="col-12 mt-2">
                          <BaseInput
                            name="remarks"
                            label={REMARKS_LABEL}
                            type="textarea"
                            value={formik.values.remarks}
                            placeholder={InputPlaceHolder(REMARKS_LABEL)}
                            onChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            error={formik.errors.remarks}
                            touched={formik.touched.remarks}
                          />
                        </div>
                      </div>

                      <span>
                        {CURRENT_OUTSTANDING_AMOUNT_LABEL}
                        <span id="outstanding-amount">
                          <i className="mdi mdi-currency-rupee"></i>
                          {formik.values.out_standing_amount}
                        </span>
                      </span>
                      <div className="table-responsive mt-2">
                        {loader || Array.isArray(history?.orderHistory) ? (
                          <TableContainer
                            customPageSize={customPageSize}
                            setCustomPageSize={setCustomPageSize}
                            isGlobalFilter={false}
                            data={history?.orderHistory || []}
                            columns={orderColumns}
                            tableClass="table bg-white"
                            manualPagination={true}
                            manualFiltering={false}
                            tableLoader={loader}
                            hasManualPagination={false}
                            currentPage={1}
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            totalNumberOfRows={totalNumberOfRows}
                            fetchData={() => {}}
                            fetchSortingData={() => {}}
                          />
                        ) : (
                          <div className="py-4 text-center">
                            <div>
                              <span className="fs-1 text-success">
                                <RiSearchLine />
                              </span>
                            </div>
                            <div className="mt-4">
                              <h5>{notFound.dataNotFound}</h5>
                              <p className="text-muted">
                                {notFound.thereIsNoDataFound}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/purchase-order")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPurchaseOrderPayment;
